import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { filesApi } from '@texas/api/endpoints/filesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { PdfPreview } from '@texas/components/shared/files/PdfPreview';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { DocumentDropzone } from '@texas/components/shared/dropzone/DocumentDropzone';
import { FileDropzone } from '@texas/components/shared/dropzone/FileDropzone';
import { notImageValidator } from '@texas/components/shared/dropzone/shared';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { FileUpload } from '@texas/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface CompositionFileProps extends SharedDisclosureProps {
  fileId: number | null;
  onRemoveFileOpen: () => void;
  onFileBrowserOpen: () => void;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  uploadFailed: boolean;
  fileUploads: FileUpload[];
  removeFileUpload: (file: FileUpload) => void;
}

export function DocumentFileModal({
  fileId,
  onRemoveFileOpen,
  onFileBrowserOpen,
  onUpload,
  isUploading,
  uploadFailed,
  fileUploads,
  removeFileUpload,
  onClose,
  isOpen,
}: CompositionFileProps) {
  const { t } = useTranslation();

  const {
    data: file,
    loading,
    set,
    refetch,
    error,
  } = useApiResource(filesApi.getFileLink);

  useEffect(() => {
    if (fileId) refetch(fileId);
    else set(null);
  }, [fileId, refetch, set]);

  return (
    <>
      <Modal onClose={onClose} isOpen={isOpen} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader fontWeight="bold">
            {t('compositionGroup.option.files.layout')}
          </ModalHeader>
          <ModalBody pb={6}>
            {loading && <Spinner />}
            {error && <ErrorDetails error={error} />}
            {file && (
              <Flex direction="column" w="100%" gap={4}>
                <Flex minW="50%" maxW="fit-content">
                  <DocumentDropzone
                    document={file}
                    allowEdit={true}
                    onFileBrowserOpen={onFileBrowserOpen}
                    onRemovefileOpen={onRemoveFileOpen}
                    onUpload={(files) => onUpload(files)}
                    isUploading={isUploading}
                    uploadFailed={uploadFailed}
                    fileUploads={fileUploads}
                    removeFileUpload={removeFileUpload}
                  />
                </Flex>
                {file.extension === '.pdf' && (
                  <PdfPreview
                    identifier={file.identifier}
                    title={file.originalName}
                  />
                )}
              </Flex>
            )}
            {!file && !loading && (
              <Flex direction="column" gap={4}>
                <Text fontStyle="italic" pl={2}>
                  {t('compositionGroup.option.files.noLayout')}
                </Text>
                <FileDropzone
                  isUploading={isUploading}
                  uploadFailed={uploadFailed}
                  fileUploads={fileUploads}
                  removeFileUpload={removeFileUpload}
                  onUpload={(files) => onUpload(files)}
                  onFileBrowserOpen={onFileBrowserOpen}
                  validator={(file) => notImageValidator(file, t)}
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
